import {
  MEDIA_TYPE_LABELS,
  type MediaItem,
  type MediaOrientation,
  MediaType,
  POST_PLACE_LABELS,
  formatDate,
} from '@deizi/shared';
import { useState } from 'react';
import { Link } from 'react-router';
import { Drawer } from '../../components/Overlays';
import { ErrorBox } from '../../components/States';
import { Combobox } from '../../components/Combobox';
import { productOption } from '../../components/pickers';
import { formatBytes } from '../../lib/backups-api';
import { type LibraryFilters, useMediaLibrary } from '../../lib/media-api';
import { useProducts } from '../../lib/products-api';
import { useIsPhone } from '../../lib/use-media';
import { MediaDetail, MediaTile } from './MediaDetail';

const FORMATS: { key: MediaOrientation | ''; label: string; aspect: string }[] = [
  { key: '', label: 'Todos', aspect: '1/1' },
  { key: 'vertical', label: 'Vertical 9:16', aspect: '9/16' },
  { key: 'feed', label: 'Feed 4:5', aspect: '4/5' },
  { key: 'square', label: 'Quadrado', aspect: '1/1' },
  { key: 'horizontal', label: 'Horizontal', aspect: '16/9' },
];

const aspectOf = (m: MediaItem) =>
  m.width && m.height ? `${m.width}/${m.height}` : m.kind === 'video' ? '9/16' : '4/5';

const DEFAULTS: LibraryFilters = {
  type: 'timelapse',
  orientation: 'vertical',
  kind: '',
  productId: '',
  notPosted: true,
  approved: '',
};

/** "3 timelapses verticais de Vaso Ondas ainda não postados" (board Biblioteca-Midia). */
export function summaryOf(n: number, f: LibraryFilters, productName: string | null): string {
  const one = n === 1;
  const what = f.type
    ? `${MEDIA_TYPE_LABELS[f.type].toLowerCase()}${one ? '' : 's'}`
    : one
      ? 'item'
      : 'itens';
  const shape = {
    '': '',
    vertical: one ? ' vertical' : ' verticais',
    feed: ' no formato feed',
    square: one ? ' quadrado' : ' quadrados',
    horizontal: one ? ' horizontal' : ' horizontais',
  }[f.orientation];
  return `${n} ${what}${shape}${productName ? ` de ${productName}` : ''}${f.notPosted ? ` ainda não postado${one ? '' : 's'}` : ''}`;
}

function caption(m: MediaItem): string {
  const date = formatDate(m.capturedAt ?? m.createdAt).slice(0, 5);
  const first = m.posts[0];
  if (first) return `${date} · postado: ${POST_PLACE_LABELS[first.where]}`;
  if (m.status !== 'ready') return `${date} · ${m.status === 'failed' ? 'falhou' : 'processando'}`;
  if (m.type === 'customer' && m.customerConsent === 'pending')
    return `${date} · aguardando autorização`;
  if (m.originalPruned) return `${date} · só prévia guardada`;
  return `${date} · ${m.approvedSocial ? 'aprovado p/ redes' : 'não aprovado'}`;
}

/** Biblioteca de mídia `/midia` (board Biblioteca-Midia). */
export function MediaLibraryPage() {
  const phone = useIsPhone();
  const [f, setF] = useState<LibraryFilters>(DEFAULTS);
  const [selId, setSelId] = useState<string | null>(null);
  // Format counts come from the same filters without the format, so fetch without it.
  const base = useMediaLibrary({ ...f, orientation: '' });
  const everything = useMediaLibrary({ ...DEFAULTS, type: '', orientation: '', notPosted: false });
  const products = useProducts({ segment: 'all', category: '', material: '' });
  const set = (p: Partial<LibraryFilters>) => setF((x) => ({ ...x, ...p }));

  const all = base.data?.data ?? [];
  const list = all.filter((m) => !f.orientation || m.orientation === f.orientation);
  const sel =
    all.find((m) => m.id === selId) ?? everything.data?.data.find((m) => m.id === selId) ?? null;
  const productName = products.data?.data.find((p) => p.id === f.productId)?.name ?? null;
  const totals = everything.data?.totals;
  const empty = everything.isSuccess && everything.data.totals.count === 0;

  return (
    <>
      <div className="pagehead">
        <div>
          <div className="crumb">Catálogo</div>
          <h1>Biblioteca de mídia</h1>
        </div>
        <div className="row wrap" style={{ gap: 16 }}>
          {totals ? (
            <span className="sm muted">
              {totals.count} {totals.count === 1 ? 'item' : 'itens'} · {formatBytes(totals.bytes)},
              vídeos {formatBytes(totals.videoBytes)}
            </span>
          ) : null}
          {phone ? (
            <Link className="btn p" to="/midia/enviar">
              Enviar fotos e vídeos
            </Link>
          ) : (
            <div className="col" style={{ gap: 2, alignItems: 'flex-end' }}>
              <button type="button" className="btn" disabled title="Fase 3">
                Exportar com marca · em breve
              </button>
              <span className="xs muted">Fase 3 · usa o kit de marca de Configurações</span>
            </div>
          )}
        </div>
      </div>

      {base.isError || everything.isError ? (
        <div className="card">
          <ErrorBox
            title="Não foi possível carregar a biblioteca"
            onRetry={() => {
              void base.refetch();
              void everything.refetch();
            }}
          />
        </div>
      ) : base.isPending || everything.isPending ? (
        <div className="col" aria-busy="true" style={{ gap: 16 }}>
          <div className="skel" style={{ height: 44, maxWidth: 860 }} />
          <div
            style={{
              display: 'grid',
              gap: 14,
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(170px, 100%), 1fr))',
            }}
          >
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="skel" style={{ aspectRatio: '9/16' }} />
            ))}
          </div>
        </div>
      ) : empty ? (
        <div className="card">
          <div className="empty">
            <div className="emptyart" />
            <h2>Nenhuma foto ou vídeo ainda</h2>
            <p>Fotos e timelapses enviados pelos produtos aparecem aqui, prontos para postar.</p>
            <div className="row wrap" style={{ gap: 10, justifyContent: 'center' }}>
              <Link className="btn p" to="/midia/enviar">
                Enviar pelo celular
              </Link>
              <Link className="btn" to="/produtos">
                Abrir um produto
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <div className="col" style={{ gap: 16 }}>
          <div className="row wrap" style={{ gap: 10 }}>
            <select
              className="input"
              aria-label="Tipo"
              style={{ width: 160 }}
              value={f.type}
              onChange={(e) => set({ type: e.target.value ? MediaType.parse(e.target.value) : '' })}
            >
              <option value="">Todos os tipos</option>
              {MediaType.options.map((t) => (
                <option key={t} value={t}>
                  {MEDIA_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
            <div
              className="seg"
              role="radiogroup"
              aria-label="Formato"
              style={{ maxWidth: '100%', overflowX: 'auto' }}
            >
              {FORMATS.map((x) => (
                <button
                  key={x.key}
                  type="button"
                  role="radio"
                  aria-checked={f.orientation === x.key}
                  className={f.orientation === x.key ? 'on' : ''}
                  onClick={() => set({ orientation: x.key })}
                >
                  {x.label}{' '}
                  <span className="tab" style={{ opacity: 0.75 }}>
                    {all.filter((m) => !x.key || m.orientation === x.key).length}
                  </span>
                </button>
              ))}
            </div>
            <Combobox
              ariaLabel="Produto"
              style={{ width: 220 }}
              value={f.productId}
              options={[
                { value: '', label: 'Todos os produtos', pinned: 'top' },
                ...(products.data?.data ?? []).map(productOption),
              ]}
              onChange={(v) => set({ productId: v })}
            />
            <button
              type="button"
              className="check sm"
              style={{ marginLeft: 6 }}
              role="switch"
              aria-checked={f.notPosted}
              onClick={() => set({ notPosted: !f.notPosted })}
            >
              <span className={f.notPosted ? 'toggle on' : 'toggle'} aria-hidden="true" />
              Ainda não postado
            </button>
          </div>
          <p className="sm muted" aria-live="polite">
            {summaryOf(list.length, f, productName)}
          </p>
          {list.length === 0 ? (
            <div className="card sunk row wrap between" style={{ gap: 12, padding: 16 }}>
              <div>
                <p style={{ fontWeight: 500 }}>Nada com esses filtros</p>
                <p className="sm muted">Tente outro formato ou desligue “Ainda não postado”.</p>
              </div>
              <button
                type="button"
                className="btn sm"
                onClick={() => setF({ ...DEFAULTS, type: '', orientation: '', notPosted: false })}
              >
                Limpar filtros
              </button>
            </div>
          ) : null}
          <div
            style={{
              display: 'grid',
              gap: 14,
              alignItems: 'start',
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(170px, 100%), 1fr))',
            }}
          >
            {list.map((m) => (
              <div key={m.id} className="col" style={{ gap: 6 }}>
                <MediaTile
                  item={m}
                  aspect={aspectOf(m)}
                  selected={selId === m.id}
                  onPick={() => setSelId(m.id)}
                  tag={MEDIA_TYPE_LABELS[m.type]}
                  showCover={false}
                  corner={m.posts.length ? <span className="posted">Postado</span> : null}
                />
                <span className="sm">
                  <b style={{ fontWeight: 500 }}>
                    {m.product.name}
                    {m.variant ? ` ${m.variant.name}` : ''}
                  </b>
                  <br />
                  <span className="xs muted">{caption(m)}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {sel ? (
        <Drawer
          kicker={`${MEDIA_TYPE_LABELS[sel.type]} · ${formatDate(sel.capturedAt ?? sel.createdAt)}`}
          title={`${sel.product.name}${sel.variant ? ` ${sel.variant.name}` : ''}`}
          onClose={() => setSelId(null)}
        >
          <MediaDetail
            item={sel}
            position={null}
            total={0}
            variants={null}
            showProduct
            bare
            onClose={() => setSelId(null)}
          />
          <div className="row wrap" style={{ gap: 10 }}>
            <Link className="btn p" to={`/produtos/${sel.product.id}/midia`}>
              Abrir produto
            </Link>
            <button type="button" className="btn" disabled title="Fase 3">
              Exportar com marca · Fase 3
            </button>
          </div>
        </Drawer>
      ) : null}
    </>
  );
}
